import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  ReactNode,
} from 'react';
import { Admin } from '../../domain/models/Admin';
import { AuthService } from '../services/AuthService';
import { adminPreferencesStorage } from '../../infrastructure/storage/AdminPreferencesStorage';
import { CookieManager } from '../../infrastructure/storage/CookieManager';
import { AUTH_CONFIG } from '../../../../core/config/auth.config';

/**
 * Authentication state interface
 */
interface AuthState {
  admin: Admin | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: Error | null;
}

/**
 * Authentication context interface
 */
interface AuthContextValue extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  loadAdmin: () => Promise<void>;
  clearError: () => void;
}

/**
 * Create the authentication context
 */
const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/**
 * AuthProvider props
 */
interface AuthProviderProps {
  children: ReactNode;
  authService: AuthService;
}

/**
 * AuthProvider component
 * Manages authentication state and provides auth operations
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children, authService }) => {
  const [state, setState] = useState<AuthState>({
    admin: null,
    isAuthenticated: false,
    isLoading: true, // Start as loading to check for existing session
    error: null,
  });

  // Ref to prevent double execution in React StrictMode
  const isRestoringSession = useRef(false);

  /**
   * Login function
   */
  const login = useCallback(
    async (email: string, password: string): Promise<void> => {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      try {
        const result = await authService.login(email, password);

        adminPreferencesStorage.save(result.admin.language, result.admin.timezone);
        setState({
          admin: result.admin,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
      } catch (error) {
        setState({
          admin: null,
          isAuthenticated: false,
          isLoading: false,
          error: error instanceof Error ? error : new Error('Login failed'),
        });
        throw error;
      }
    },
    [authService],
  );

  /**
   * Logout function
   */
  const logout = useCallback(async (): Promise<void> => {
    setState((prev) => ({ ...prev, isLoading: true, error: null }));

    try {
      await authService.logout();

      adminPreferencesStorage.clear();
      CookieManager.deleteCookie(AUTH_CONFIG.refreshTokenFlagCookie);
      setState({
        admin: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      });
    } catch (error) {
      // Even if logout fails on the server, clear local state
      adminPreferencesStorage.clear();
      CookieManager.deleteCookie(AUTH_CONFIG.refreshTokenFlagCookie);
      setState({
        admin: null,
        isAuthenticated: false,
        isLoading: false,
        error: error instanceof Error ? error : new Error('Logout failed'),
      });
    }
  }, [authService]);

  /**
   * Load admin data (lazy-loaded when needed)
   * Can be called from protected routes that need admin information
   */
  const loadAdmin = useCallback(async (): Promise<void> => {
    setState((prev) => {
      // Skip if admin data is already loaded
      if (prev.admin !== null) {
        return prev;
      }

      // Skip if not authenticated
      if (!prev.isAuthenticated) {
        return prev;
      }

      // Return unchanged state, will fetch below
      return prev;
    });

    try {
      const admin = await authService.getCurrentAdmin();
      setState((prev) => {
        // Only update if still authenticated and admin not already loaded
        if (prev.isAuthenticated && prev.admin === null) {
          return {
            ...prev,
            admin,
            error: null,
          };
        }
        return prev;
      });
    } catch (error) {
      // If loading admin fails, don't change authentication state
      // Just set the error
      setState((prev) => ({
        ...prev,
        error: error instanceof Error ? error : new Error('Failed to load admin data'),
      }));
    }
  }, [authService]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));
  }, []);

  /**
   * Restore session on mount by checking refresh token validity
   * Only runs once on mount (protected against React StrictMode double execution)
   *
   * Checks the non-httpOnly flag cookie first to avoid a wasted /auth/refresh
   * request when no refresh token cookie exists.
   */
  useEffect(() => {
    const restoreSession = async () => {
      // Prevent double execution in React StrictMode
      if (isRestoringSession.current) {
        return;
      }
      isRestoringSession.current = true;

      // No refresh token cookie — skip the request entirely
      if (!CookieManager.hasCookie(AUTH_CONFIG.refreshTokenFlagCookie)) {
        setState({
          admin: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
        return;
      }

      try {
        // Check if refresh token is valid by attempting to refresh
        await authService.refreshToken();

        // Fetch admin data
        const admin = await authService.getCurrentAdmin();

        // Mark user as authenticated with admin data
        adminPreferencesStorage.save(admin.language, admin.timezone);
        setState({
          admin,
          isAuthenticated: true,
          isLoading: false,
          error: null,
        });
      } catch (_error) {
        // No valid refresh token - user needs to log in
        CookieManager.deleteCookie(AUTH_CONFIG.refreshTokenFlagCookie);
        setState({
          admin: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
        });
      }
    };

    restoreSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  /**
   * Memoize context value to prevent unnecessary re-renders
   */
  const value = useMemo(
    () => ({
      ...state,
      login,
      logout,
      loadAdmin,
      clearError,
    }),
    [state, login, logout, loadAdmin, clearError],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * useAuth hook
 * Provides access to authentication context
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};
